/**
 * CoursSalle.jsx — Hub des 20 cours en salle (ECSR)
 *
 * Style Apple tactile pro :
 *  - Header glassmorphique avec compteur de cours et filtres niveau
 *  - Grille de cards arrondies hover-effect, icônes Lucide
 *  - Click sur une card → affichage via <PedagogicalCanvas trameId="salle">
 *    avec mode présentation full-screen
 *
 * Trame officielle : TRAME_SALLE (REMC, 7 étapes).
 * Les cours legacy (format ECSR 6 étapes) sont convertis via
 * `getCoursSalleAsCanvas()` à la volée.
 */
import React, { useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import {
  ArrowLeft, ArrowRight, BookOpen, Filter, Sparkles, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PedagogicalCanvas from '../components/teacher/PedagogicalCanvas';
import PresentationModeOverlay from '../components/teacher/PresentationMode';
import { coursSalle, getCoursSalleAsCanvas } from '../data/coursSalle';
import useSeo from '../hooks/useSeo';
import AudienceContextBanner from '../components/AudienceContextBanner';

/* Liste des niveaux pour filtrage */
const NIVEAUX = ['Tous niveaux', 'AM / BSR', 'A1 / 125', 'A2 / 125', 'A2 / A'];

function CoursCard({ cours, onSelect }) {
  const Icon = Icons[cours.icon] || Icons.BookOpen;
  return (
    <button
      type="button"
      onClick={() => onSelect(cours)}
      data-testid={`cours-salle-card-${cours.id}`}
      className="group relative overflow-hidden rounded-3xl bg-card border border-border/60 p-5 sm:p-6 text-left flex flex-col min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98]"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -16px rgba(0,0,0,0.12)' }}
    >
      <div
        className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
        style={{ background: '#1d4ed8' }}
        aria-hidden="true"
      />
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{ background: '#1d4ed8', boxShadow: '0 8px 20px -8px #1d4ed855' }}
          >
            <Icon className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] rounded-full px-2.5 py-1 bg-[#1d4ed8]/15 text-[#1d4ed8]">
              {cours.id.toUpperCase()}
            </span>
            <span className="text-[10px] text-muted-foreground font-bold">{cours.duree}</span>
          </div>
        </div>
        <h3 className="font-black text-base sm:text-lg leading-tight tracking-tight">
          {cours.theme}
        </h3>
        <div className="text-[10px] font-black uppercase tracking-[0.14em] mt-1.5 text-[#1d4ed8]">
          {cours.niveau}
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mt-2.5 leading-relaxed flex-1 line-clamp-3">
          {cours.objectif}
        </p>
        {cours.phraseCle && (
          <p
            className="text-xs italic font-bold mt-2 text-[#1d4ed8] line-clamp-2"
            title={cours.phraseCle}
          >
            « {cours.phraseCle} »
          </p>
        )}
        <div className="flex items-center gap-1.5 text-xs sm:text-sm font-bold mt-3 group-hover:gap-2.5 transition-all duration-300 text-[#1d4ed8]">
          Ouvrir le cours
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

export default function CoursSalle() {
  const [activeCours, setActiveCours] = useState(null);
  const [presenting, setPresenting] = useState(false);
  const [filterNiveau, setFilterNiveau] = useState('all');

  useSeo({
    title: 'Cours en salle · 20 fiches ECSR · Espace enseignant',
    description:
      "Bibliothèque de 20 fiches cours type ECSR (théorie en salle) conformes à la trame ECSR : Accueil · Objectif · Représentations · Cours · Vérification · Synthèse · Évaluation.",
    path: '/cours-salle',
  });

  const filtered = useMemo(() => {
    if (filterNiveau === 'all') return coursSalle;
    return coursSalle.filter((c) => c.niveau === filterNiveau);
  }, [filterNiveau]);

  const canvasData = useMemo(
    () => (activeCours ? getCoursSalleAsCanvas(activeCours) : null),
    [activeCours],
  );

  /* ─────────── Vue détail (PedagogicalCanvas) ─────────── */
  if (activeCours && canvasData) {
    return (
      <div className="space-y-4" data-testid="cours-salle-detail">
        <button
          type="button"
          onClick={() => {
            setActiveCours(null);
            setPresenting(false);
          }}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground"
          data-testid="back-to-cours-salle-hub"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la bibliothèque
        </button>

        <PedagogicalCanvas
          trameId="salle"
          title={canvasData.title}
          kicker={canvasData.kicker}
          meta={canvasData.meta}
          steps={canvasData.steps}
          accent={canvasData.accent}
          testid={`canevas-${activeCours.id}`}
          onPresentToggle={() => setPresenting(true)}
        />

        {presenting && (
          <PresentationModeOverlay
            trameId="salle"
            title={canvasData.title}
            kicker={canvasData.kicker}
            meta={canvasData.meta}
            steps={canvasData.steps}
            onClose={() => setPresenting(false)}
          />
        )}
      </div>
    );
  }

  /* ─────────── Vue hub Apple tactile pro ─────────── */
  return (
    <div className="space-y-5" data-testid="cours-salle-hub">
      {/* HEADER tactile pro */}
      <header
        className="relative overflow-hidden rounded-3xl border border-border/60 bg-card"
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(29,78,216,0.25)' }}
      >
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: '#1d4ed8' }}
          aria-hidden="true"
        />
        <div className="relative p-5 sm:p-7">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#1d4ed8]/15 border border-[#1d4ed8]/30 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#1d4ed8] mb-2">
            <BookOpen className="w-3 h-3" /> Cours · Salle théorie
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            20 fiches cours en salle
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl">
            Bibliothèque de cours théoriques ECSR, conformes à la trame officielle ECSR
            (Accueil · Objectif · Représentations · Cours · Vérification · Synthèse · Évaluation).
            Mode présentation full-screen pour donner cours.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
              <Filter className="w-3 h-3" /> Niveau
            </span>
            <button
              type="button"
              onClick={() => setFilterNiveau('all')}
              className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider transition-colors ${
                filterNiveau === 'all' ? 'bg-[#1d4ed8] text-white' : 'bg-muted text-muted-foreground'
              }`}
              data-testid="filter-all"
            >
              Tous ({coursSalle.length})
            </button>
            {NIVEAUX.map((n) => {
              const count = coursSalle.filter((c) => c.niveau === n).length;
              if (count === 0) return null;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setFilterNiveau(n)}
                  className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider transition-colors ${
                    filterNiveau === n ? 'bg-[#1d4ed8] text-white' : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`filter-${n.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {n} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Bandeau d'adaptation par audience */}
      <AudienceContextBanner
        pageHint="Les cours en salle servent à 2 publics : tu donnes cours à tes élèves ou tu prépares ton CCS ?"
        contexts={{
          enseignant: {
            title: 'Tes 20 fiches de cours prêtes à projeter',
            bullets: [
              'Chaque fiche suit la trame officielle ECSR (Accueil → Objectif → … → Synthèse → Évaluation).',
              'Active le mode présentation full-screen pour animer en salle.',
              'Filtre par niveau (AM/BSR, A1, A2, etc.) selon le profil de ta classe.',
            ],
            cta: 'Voir le canevas pédagogique',
            ctaTo: '/enseignant/canevas-pedagogique',
          },
          'futur-enseignant': {
            title: 'Modèles de cours-types à étudier avant le jury',
            bullets: [
              'Repère la structure ECSR appliquée : c\'est exactement ce que le jury attend.',
              'Analyse comment chaque cours articule les 7 étapes officielles.',
              'Réutilise la trame pour construire ta fiche séance d\'examen.',
            ],
            cta: 'Aller à la préparation CCS',
            ctaTo: '/preparation-ccs',
          },
        }}
        showOnEleve={false}
      />

      {/* GRILLE COURS */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center">
          <Layers className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Aucun cours pour ce niveau.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <CoursCard key={c.id} cours={c} onSelect={setActiveCours} />
          ))}
        </div>
      )}

      {/* INFO */}
      <div className="rounded-2xl bg-muted/30 border border-border/40 p-4 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-[#1d4ed8] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Format ECSR ↔ REMC 7 étapes :</strong>{' '}
          Chaque cours est servi via la trame officielle ECSR. L&apos;étape « Objectif » est
          dérivée automatiquement, les dangers/bons réflexes sont fusionnés dans l&apos;étape
          « Cours » pour un rendu uniforme avec les fiches Plateau et Circulation.
        </div>
      </div>

      <div className="flex justify-center">
        <Button asChild variant="outline" className="gap-1.5">
          <a href="/enseignant">
            <ArrowLeft className="w-4 h-4" /> Retour à l&apos;espace enseignant
          </a>
        </Button>
      </div>
    </div>
  );
}
